import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { resetIncidents } from 'store/entities/incidents/incidentsSlice';
import { fetchIncidents } from 'store/entities/incidents/incidentsThunk';
import { selectIncidents } from 'store/selectors';
import { useForm } from 'react-hook-form';
import { ApiIncidentManageFilters } from 'services/incidentsEndPoints';

/**
 * Interface for the search form's default values.
 */
interface IIncidentSearch {
  keyword: string; // The search keyword for filtering incidents.
}

/**
 * Custom hook to fetch and manage incidents data.
 *
 * This hook:
 * - Initializes a form with a `keyword` field for searching incidents.
 * - Watches the `keyword` field for changes and triggers a fetch operation with a debounce.
 * - Resets the incidents state before fetching new data.
 *
 * @returns {object} The `control` object from `react-hook-form` for managing the form.
 */
function useFetchIncident() {
  const dispatch = useAppDispatch(); // Dispatch function for Redux actions.
  const { pageLimit, page } = useAppSelector(selectIncidents); // Select pagination data from the Redux store.

  // Initialize the form with a default `keyword` value.
  const { control, watch } = useForm<IIncidentSearch>({
    defaultValues: {
      keyword: '',
    },
  });

  // Watch the `keyword` field for changes.
  const keyword = watch('keyword');

  useEffect(() => {
    // Debounce the fetch operation to avoid excessive API calls.
    const delayKeyPressFn = setTimeout(() => {
      dispatch(resetIncidents()); // Reset the incidents state.

      // Prepare the filters for the API request.
      const filters: ApiIncidentManageFilters = {
        keyword: keyword,
        limit: pageLimit,
        offset: page,
      };

      // Dispatch the fetch action with the prepared filters.
      dispatch(fetchIncidents(filters));
    }, 1000);

    // Cleanup the debounce timer on unmount or dependency change.
    return () => clearTimeout(delayKeyPressFn);
    // eslint-disable-next-line
  }, [keyword, page]); // Dependencies: `keyword` and `page`.

  // Return the `control` object for managing the form.
  return control;
}

export default useFetchIncident;
